/*
 * ------------ Theme Common JS ------------
 */

if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  variantChange: "variant-change",
  cartError: "cart-error",
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

// Click icon-slide animation for static slider nav buttons.
// Skips section-level nav inside .js-section-card-slider, but still runs for
// product card media controls (which live inside that slider too).
document.addEventListener("click", function (e) {
  const btn = e.target.closest(".slider-nav-button");
  if (
    !btn ||
    (btn.closest(".js-section-card-slider") &&
      !btn.closest(".product-card__media-controls")) ||
    btn.classList.contains("is-clicked")
  )
    return;

  btn.classList.add("is-clicked");

  const trail = btn.querySelector(".slider-nav-button__arrow--trail");
  if (!trail) {
    btn.classList.remove("is-clicked");
    return;
  }

  function onTransitionEnd(e) {
    if (e.propertyName !== "transform") return;
    trail.removeEventListener("transitionend", onTransitionEnd);
    const arrows = btn.querySelectorAll(".slider-nav-button__arrow");
    arrows.forEach((a) => {
      a.style.transition = "none";
    });
    btn.classList.remove("is-clicked");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        arrows.forEach((a) => {
          a.style.transition = "";
        });
      });
    });
  }

  trail.addEventListener("transitionend", onTransitionEnd);
});

// Minimum pointer travel (px) before a press on the cursor's tracked area
// counts as a drag/swipe rather than a tap — below this, the slide never changes.
const CURSOR_DRAG_THRESHOLD = 10;

// Cursor-following nav arrow for card sliders. Ports the RAF-eased follow +
// activate/deactivate lifecycle from Egoiste's PopularProductsSection#initCursor
// (ashstonestudios/egoiste, src/js/popular-products.js), adapted to this theme's
// --cursor-x/--cursor-y positioning, prev/next slide direction and
// drag-to-navigate. The (min-width: 750px) breakpoint gate lives inside the
// class (instead of duplicated at every call site) so any caller gets the same
// responsive enable/disable for free — just call initCardSliderCursor and store
// the returned destroy fn for section teardown (rule D).
class SliderDragCursor {
  // Players that own their own pointer surface once running, plus the controls
  // that start and drive them — the cursor hides over these so it never sits on
  // top of a button. The poster SURFACE is deliberately not included: before it
  // is started the slide behaves like an image, and hiding the cursor across the
  // whole poster left a gallery of nothing but video/3D posters with no visible
  // way to page. Only the control itself takes the cursor away.
  static MEDIA_SELECTOR =
    "video, model-viewer, iframe, .deferred-media__poster-button, .shopify-model-viewer-ui__button";

  // Whether this slider can grow a player at all. Kept separate from the
  // selector above because deferred media only swaps its poster for the real
  // element when the shopper starts it, long after the flag is read.
  static MEDIA_HOST_SELECTOR = ".deferred-media";

  constructor(trackingEl, cursorEl, swiper) {
    this.trackingEl = trackingEl;
    this.cursorEl = cursorEl;
    this.swiper = swiper;

    this.targetX = 0;
    this.targetY = 0;
    this.x = 0;
    this.y = 0;
    this.raf = null;
    this.rect = null;
    this.visible = false;
    this.positioned = false;
    this.eventController = null;

    this.dragPointerId = null;
    this.dragStartX = 0;
    this.dragStartY = 0;

    this.pointerX = 0;
    this.pointerY = 0;
    this.cardZones = [];
    this.hasMedia = false;

    this.resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(this.onResize)
        : null;

    this.mq = window.matchMedia("(min-width: 750px)");
    this.mq.addEventListener("change", this.sync);

    // Swiper locks itself while every slide fits the row (too few blocks for the
    // Cards per row setting), and a cursor that navigates nothing reads as broken.
    // lock/unlock fire on init and on every breakpoint change, so the gate follows.
    this.swiper?.on("lock", this.sync);
    this.swiper?.on("unlock", this.sync);

    this.sync();
  }

  sync = () => {
    if (this.mq.matches && !this.swiper?.isLocked) this.activate();
    else this.deactivate();
  };

  updateRect = () => {
    this.rect = this.trackingEl.getBoundingClientRect();
  };

  onResize = () => {
    this.refreshDirection();
    this.updateRect();
    this.refreshCardZones();
  };

  // A card hides the cursor across its FULL footprint — border box plus the
  // inline-gutter margin — so it stays hidden while crossing from one card to
  // the next. Margins sit outside the border box, so closest()/:hover can't
  // see them and the zone is hit-tested geometrically instead. Both physical
  // margins are read because the gutter is margin-inline-end (right in LTR,
  // left in RTL), and its width is breakpoint-dependent — hence the resize
  // refresh. Rects are re-read per frame since slides move under the pointer.
  refreshCardZones() {
    this.hasMedia = !!this.trackingEl.querySelector(
      SliderDragCursor.MEDIA_HOST_SELECTOR
    );
    this.cardZones = Array.from(
      this.trackingEl.querySelectorAll(".section-card-item")
    ).map((el) => {
      const style = getComputedStyle(el);
      return {
        el,
        marginLeft: parseFloat(style.marginLeft) || 0,
        marginRight: parseFloat(style.marginRight) || 0,
      };
    });
  }

  // A running player owns the area it covers: its controls sit right where the
  // cursor glyph lands, and the glyph would read as the thing to click.
  // Hit-tested per move rather than pre-measured, because the player element
  // appears only once the shopper starts it.
  isOverMedia(x, y) {
    if (!this.hasMedia) return false;
    const el = document.elementFromPoint(x, y);
    return !!el?.closest(SliderDragCursor.MEDIA_SELECTOR);
  }

  isOverCardZone(x, y) {
    return this.cardZones.some(({ el, marginLeft, marginRight }) => {
      const rect = el.getBoundingClientRect();
      return (
        x >= rect.left - marginLeft &&
        x <= rect.right + marginRight &&
        y >= rect.top &&
        y <= rect.bottom
      );
    });
  }

  updateVisibility() {
    if (
      this.isOverCardZone(this.pointerX, this.pointerY) ||
      this.isOverMedia(this.pointerX, this.pointerY)
    ) {
      this.hide();
    } else {
      this.show();
    }
  }

  // Read from the tracked element rather than the document: it is the direction
  // Swiper itself resolves, and it stays right for a section that sets its own.
  refreshDirection() {
    this.isRtl = getComputedStyle(this.trackingEl).direction === "rtl";
  }

  activate() {
    if (this.eventController) return;
    this.eventController = new AbortController();
    const { signal } = this.eventController;

    this.refreshDirection();
    this.updateRect();
    this.refreshCardZones();

    this.trackingEl.addEventListener("pointermove", this.onMove, { signal });
    this.trackingEl.addEventListener("pointerleave", this.onLeave, {
      signal,
    });
    // Capture phase: Swiper's scrollbar module calls stopPropagation() on its
    // own pointerdown handler (bound on .slider-scrollbar), which would
    // otherwise stop these bubble-phase listeners from ever firing when the
    // press starts inside .section-card-controls (e.g. on
    // .slider-scrollbar-main-hitbox). Capture runs before that stopPropagation.
    this.trackingEl.addEventListener("pointerdown", this.onPointerDown, {
      signal,
      capture: true,
    });
    this.trackingEl.addEventListener("pointerup", this.onPointerUp, {
      signal,
      capture: true,
    });
    this.trackingEl.addEventListener("pointercancel", this.onPointerCancel, {
      signal,
      capture: true,
    });
    window.addEventListener("scroll", this.updateRect, {
      passive: true,
      signal,
    });

    this.resizeObserver?.observe(this.trackingEl);
  }

  deactivate() {
    if (!this.eventController) return;
    this.eventController.abort();
    this.eventController = null;
    this.resizeObserver?.disconnect();

    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    this.hide();
    this.cursorEl.classList.remove("is-dragging");
    this.rect = null;
    this.cardZones = [];
    this.positioned = false;
    this.dragPointerId = null;
  }

  show() {
    if (this.visible) return;
    this.cursorEl.classList.add("is-visible");
    this.visible = true;
  }

  hide() {
    if (!this.visible) return;
    this.cursorEl.classList.remove("is-visible");
    this.visible = false;
  }

  onMove = (event) => {
    const rect = this.rect;
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    this.targetX = event.clientX - rect.left;
    this.targetY = event.clientY - rect.top;

    // In RTL the row reads right to left, so the near half is the NEXT one — the
    // shared cursor CSS flips the glyphs on that assumption.
    const pastMiddle = this.targetX > rect.width / 2;
    const isPrev = this.isRtl ? pastMiddle : !pastMiddle;
    this.cursorEl.classList.toggle("slider-drag-cursor--prev", isPrev);
    this.cursorEl.classList.toggle("slider-drag-cursor--next", !isPrev);

    if (!this.positioned) {
      this.x = this.targetX;
      this.y = this.targetY;
      this.positioned = true;
    }

    if (!this.raf) {
      this.raf = requestAnimationFrame(this.tick);
    }

    this.updateVisibility();
  };

  onLeave = () => {
    this.hide();
    this.cursorEl.classList.remove("is-dragging");
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    this.positioned = false;
    this.dragPointerId = null;
  };

  // Slide navigation is drag/swipe-only — a press that never travels past
  // CURSOR_DRAG_THRESHOLD is a tap (e.g. a product-card link) and must not
  // change the slide. Swiper already handles native drag directly over its own
  // root, so a press starting there is left alone here — only presses starting
  // outside the swiper (the wider gutter/controls area this cursor also
  // tracks) are handled, which keeps the two from double-advancing the slide.
  onPointerDown = (event) => {
    this.cursorEl.classList.add("is-dragging");

    if (this.dragPointerId !== null) return;
    if (
      event.target.closest(
        "a, button, .button, input, .slider-nav-button, .section-card-controls"
      )
    ) {
      return;
    }
    if (this.swiper.el?.contains(event.target)) return;

    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
  };

  onPointerUp = (event) => {
    this.cursorEl.classList.remove("is-dragging");

    if (event.pointerId !== this.dragPointerId) return;
    this.dragPointerId = null;

    const deltaX = event.clientX - this.dragStartX;
    const deltaY = event.clientY - this.dragStartY;

    if (
      Math.abs(deltaX) < CURSOR_DRAG_THRESHOLD ||
      Math.abs(deltaX) <= Math.abs(deltaY)
    ) {
      return;
    }

    this.cursorEl.classList.add("is-clicking");
    this.cursorEl.addEventListener(
      "animationend",
      () => this.cursorEl.classList.remove("is-clicking"),
      { once: true }
    );

    // Dragging against the reading direction advances: left in LTR, right in RTL.
    const draggedForward = this.isRtl ? deltaX > 0 : deltaX < 0;
    if (draggedForward) this.swiper.slideNext();
    else this.swiper.slidePrev();
  };

  onPointerCancel = (event) => {
    this.cursorEl.classList.remove("is-dragging");
    if (event.pointerId !== this.dragPointerId) return;
    this.dragPointerId = null;
  };

  tick = () => {
    const ease = 0.25;
    this.x += (this.targetX - this.x) * ease;
    this.y += (this.targetY - this.y) * ease;
    this.cursorEl.style.setProperty("--cursor-x", `${this.x}px`);
    this.cursorEl.style.setProperty("--cursor-y", `${this.y}px`);
    // Re-tested per frame, not only on pointermove: slides travel under a
    // stationary pointer, which moves the zone rather than the cursor.
    this.updateVisibility();
    this.raf = requestAnimationFrame(this.tick);
  };

  destroy() {
    this.mq.removeEventListener("change", this.sync);
    this.swiper?.off("lock", this.sync);
    this.swiper?.off("unlock", this.sync);
    this.deactivate();
  }
}

function initCardSliderCursor(section, sliderEl, swiper) {
  const cursorEl = section.querySelector(".js-section-card-cursor");
  if (!cursorEl) return null;

  // When the cursor element lives outside the swiper (e.g. as a direct child of
  // .section-card-container), track events from that wider element so pointer
  // movement over .section-card-controls is also captured and the cursor stays
  // visible seamlessly. Using parentElement is intentionally generic — it works
  // for any container without hardcoding class names.
  // When the cursor is inside the swiper, fall back to sliderEl (default behaviour).
  const trackingEl = sliderEl.contains(cursorEl)
    ? sliderEl
    : cursorEl.parentElement || sliderEl;

  const cursor = new SliderDragCursor(trackingEl, cursorEl, swiper);
  return () => cursor.destroy();
}

// Runs the callback once the element comes within rootMargin of the viewport. A long
// page carries dozens of sliders, and building them all on load (each one measures
// and lays out its track) is what blocks the main thread. Returns a cancel function.
// The theme editor builds right away: block selection jumps to off-screen slides.
function onNearViewport(el, callback, rootMargin = "400px 0px") {
  if (window.Shopify?.designMode || !("IntersectionObserver" in window)) {
    callback();
    return () => {};
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      callback();
    },
    { rootMargin }
  );
  observer.observe(el);

  return () => observer.disconnect();
}

// Per-section cleanup callbacks (cursor listeners + observer) keyed by the
// section element so re-init / unload tears the right one down (rule D).
const cardSliderCleanups = new WeakMap();

function runCardSliderCleanup(section) {
  if (!section) return;
  const fn = cardSliderCleanups.get(section);
  if (typeof fn === "function") {
    fn();
    cardSliderCleanups.delete(section);
  }
}

// Swiper's loop rotates the real slides through the DOM instead of cloning them. When
// the a11y module scrolls a focused slide into view, that rotation re-inserts the slide
// mid-wrapper: Tab picks up from the new position and wraps forever, so focus can never
// leave the slider, and re-parenting the focused slide drops the focus outright. Hold
// the rotation while focus is inside — the wrapper then keeps the order Tab entered
// with, and the browser walks the slides once and leaves on its own.
function initSliderLoopFocus(sliderEl, swiper) {
  if (typeof swiper.loopFix !== "function") return null;

  const loopFix = swiper.loopFix.bind(swiper);
  let held = false;

  const hold = () => {
    if (held) return;
    held = true;
    swiper.loopFix = () => {};
  };

  const release = () => {
    if (!held) return;
    held = false;
    swiper.loopFix = loopFix;
    // Catch up on the rotation the traversal held back.
    loopFix();
  };

  const onFocusIn = () => hold();

  const onFocusOut = (event) => {
    // Still moving between slides — the hold has to outlast the whole traversal.
    if (event.relatedTarget && sliderEl.contains(event.relatedTarget)) return;
    release();
  };

  // A drag wants the wrap back at once, focus or no focus.
  const onPointerDown = () => release();

  sliderEl.addEventListener("focusin", onFocusIn);
  sliderEl.addEventListener("focusout", onFocusOut);
  sliderEl.addEventListener("pointerdown", onPointerDown);

  return () => {
    sliderEl.removeEventListener("focusin", onFocusIn);
    sliderEl.removeEventListener("focusout", onFocusOut);
    sliderEl.removeEventListener("pointerdown", onPointerDown);
    release();
  };
}

// Shared initialization script initSectionSliderLayout is used for sliders across sections (Popular products, Featured blog, Collection list, Multicolumn, etc.). Sliders can be initialized per section if needed.
function initSectionSliderLayout(section) {
  if (!section.closest("[data-section-has-card-slider='true']")) return;

  const sliderEl = section.querySelector(".js-section-card-slider");
  if (!sliderEl) return;

  // Guard re-init in the theme editor: tear down listeners + the old Swiper.
  runCardSliderCleanup(section);
  if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);

  // A grid that only slides on small screens names that range in
  // data-slider-media: the slider is built inside it and torn down outside it,
  // handing the markup back to the grid.
  const sliderMedia = sliderEl.dataset.sliderMedia;
  if (!sliderMedia) {
    let unmount = null;
    const cancel = onNearViewport(sliderEl, () => {
      unmount = mountSectionCardSlider(section, sliderEl);
    });
    cardSliderCleanups.set(section, () => {
      cancel();
      if (unmount) unmount();
    });
    return;
  }

  const mediaQuery = window.matchMedia(sliderMedia);
  let unmount = null;
  const syncToMedia = () => {
    if (mediaQuery.matches && !unmount) {
      unmount = mountSectionCardSlider(section, sliderEl);
    } else if (!mediaQuery.matches && unmount) {
      unmount();
      unmount = null;
    }
  };

  mediaQuery.addEventListener("change", syncToMedia);
  syncToMedia();

  cardSliderCleanups.set(section, () => {
    mediaQuery.removeEventListener("change", syncToMedia);
    if (unmount) unmount();
  });
}

function mountSectionCardSlider(section, sliderEl) {
  // The grid markup carries no Swiper classes, so its layout never meets
  // Swiper's own styles; they are lent only while the slider is mounted.
  const isMediaSlider = sliderEl.dataset.sliderMedia !== undefined;
  if (isMediaSlider) {
    sliderEl.classList.add("swiper");
    sliderEl
      .querySelector(".section-card-wrapper")
      ?.classList.add("swiper-wrapper");
  }

  // Slides emitted via {% content_for 'blocks' %} sit inside an outer
  // .shopify-block wrapper, so the direct children of .swiper-wrapper carry no
  // swiper-slide class and Swiper finds 0 slides. Promote those wrappers to
  // slides. No-op when the direct children are already swiper-slide, so sliders
  // that build their own slide markup are left untouched.
  const wrapperEl = sliderEl.querySelector(".swiper-wrapper");
  if (wrapperEl) {
    const directChildren = [...wrapperEl.children];
    const hasSlide = directChildren.some((el) =>
      el.classList.contains("swiper-slide")
    );
    if (!hasSlide) {
      directChildren.forEach((el) => el.classList.add("swiper-slide"));
    }
  }

  // Scope controls to the section-level slider — a product card can contain its
  // own in-card gallery .slider-pagination / .slider-nav-button, so a bare
  // section.querySelector would wrongly bind the section slider to a card's controls.
  const notInCard = (el) => el && !el.closest(".product-card");
  const paginationEl = [...section.querySelectorAll(".slider-pagination")].find(
    notInCard
  );
  const nextBtn = [
    ...section.querySelectorAll(".slider-nav-button--next"),
  ].find(notInCard);
  const prevBtn = [
    ...section.querySelectorAll(".slider-nav-button--prev"),
  ].find(notInCard);
  const hasNavigation = sliderEl.getAttribute("data-navigation") === "true";
  const hasCursorNavigation =
    sliderEl.getAttribute("data-cursor-navigation") === "true";
  const hasPagination = sliderEl.getAttribute("data-pagination") === "true";
  // Progress-bar pagination (shared control): a single full-width track whose
  // fill grows proportionally to the slider's overall progress (Swiper's native
  // "progressbar" type) instead of per-slide bullets.
  const paginationType =
    sliderEl.getAttribute("data-pagination-type") === "progress"
      ? "scrollbar"
      : "bullets";

  // When pagination type is scrollbar, find the dedicated .slider-scrollbar
  // element (scoped outside product cards, same as other controls).
  const scrollbarEl =
    paginationType === "scrollbar"
      ? [...section.querySelectorAll(".slider-scrollbar")].find(notInCard) ||
        null
      : null;

  const sliderLoop = sliderEl.getAttribute("data-loop") === "true";
  const hasSliderAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
  const pauseAutoplayOnHover =
    sliderEl.getAttribute("data-autoplay-pause-on-hover") === "true";
  const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 8000);
  const slidesPerRow = Number(sliderEl.dataset.slidesPerRow || 4);
  const slidesPerRowMobile = Number(sliderEl.dataset.slidesPerRowMobile || 1);
  // Peek sliders (one slide at a time with part of the next visible) size their
  // slides in CSS instead of splitting the row into a fixed count — they opt in
  // with data-slides-per-view="auto".
  const autoSlidesPerView = sliderEl.dataset.slidesPerView === "auto";
  // Per-section gap override: a section that wants edge-to-edge slides sets
  // data-space-between="0". Falls back to the default 12/24 gap when absent.
  const hasSpaceBetween = sliderEl.dataset.spaceBetween !== undefined;
  const spaceBetween = hasSpaceBetween
    ? Number(sliderEl.dataset.spaceBetween)
    : 12;
  const spaceBetweenWide = hasSpaceBetween ? spaceBetween : 24;
  // Ticker mode: continuous linear marquee-style scroll (delay:0 + a long linear
  // speed) instead of slide-by-slide. Drag-to-scrub stays on. Implies loop.
  const isTicker = sliderEl.getAttribute("data-ticker") === "true";
  // Opt-in: a row shorter than the view sits centred instead of leaving the
  // unfilled slots as a hole at the end.
  const centerInsufficientSlides =
    sliderEl.getAttribute("data-center-insufficient-slides") === "true";

  // Only an autoplaying slider keeps the played bullets filled (the row then reads as
  // a progress track); without autoplay the fill marks the active bullet alone.
  if (paginationEl) {
    paginationEl.classList.toggle(
      "slider-pagination--autoplay",
      hasSliderAutoplay || isTicker
    );
  }

  const slidesPerRow576 = slidesPerRow > 1 ? 2 : 1;
  // A section whose card needs more room than the ladder gives it holds a lower
  // count through the 990 band and reaches its full row at $xxls instead, the
  // same step four-up already waits for.
  const slidesPerRowLgOverride = Number(sliderEl.dataset.slidesPerRowLg);
  const slidesPerRow990 = slidesPerRowLgOverride
    ? slidesPerRowLgOverride
    : slidesPerRow > 2
      ? slidesPerRow576 + 1
      : slidesPerRow576;
  const slidesPerRowWide = slidesPerRow >= 4 ? 4 : slidesPerRow;

  const sliderSettings = {
    loop: isTicker ? true : sliderLoop,
    slidesPerView: autoSlidesPerView ? "auto" : slidesPerRowMobile,
    spaceBetween: spaceBetween,
    speed: isTicker ? 6000 : 400,
    centerInsufficientSlides: centerInsufficientSlides,
    watchSlideProgress: true,
    allowTouchMove: true,
    mousewheel: isTicker
      ? false
      : {
          forceToAxis: true,
        },
    a11y: {
      scrollOnFocus: true,
    },
    autoplay:
      isTicker || hasSliderAutoplay
        ? {
            delay: isTicker ? 0 : autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: isTicker ? true : pauseAutoplayOnHover,
          }
        : false,
    pagination:
      hasPagination && paginationType === "bullets"
        ? {
            el: paginationEl,
            type: "bullets",
            clickable: true,
            progressbarFillClass: "swiper-pagination-progress-fill",
          }
        : false,
    scrollbar:
      hasPagination && paginationType === "scrollbar" && scrollbarEl
        ? {
            el: scrollbarEl,
            grabCursor: true,
            draggable: true,
            hide: false,
          }
        : false,
    navigation: hasNavigation
      ? {
          nextEl: nextBtn,
          prevEl: prevBtn,
          disabledClass: "swiper-button-disabled",
        }
      : false,
    breakpoints: {
      576: {
        slidesPerView: autoSlidesPerView ? "auto" : slidesPerRow576,
        spaceBetween: spaceBetweenWide,
      },
      990: {
        slidesPerView: autoSlidesPerView ? "auto" : slidesPerRow990,
        spaceBetween: spaceBetweenWide,
      },
      // $xxls — the row reaches its full count here, same step as the card grid
      // ladder in slider.scss.
      1536: {
        slidesPerView: autoSlidesPerView ? "auto" : slidesPerRowWide,
        spaceBetween: spaceBetweenWide,
      },
    },
    on: {
      autoplayTimeLeft(s, time, progress) {
        if (hasPagination && paginationEl && paginationType === "bullets") {
          paginationEl.style.setProperty("--slider-progress", 1 - progress);
        }
      },
    },
  };

  const swiperInstance = new Swiper(sliderEl, sliderSettings);

  let autoplayObserver = null;
  if ((hasSliderAutoplay || isTicker) && sliderEl.swiper) {
    autoplayObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sliderEl.swiper.autoplay.resume();
        } else {
          sliderEl.swiper.autoplay.pause();
        }
      });
    });

    autoplayObserver.observe(section);
  }

  const cursorCleanup = hasCursorNavigation
    ? initCardSliderCursor(section, sliderEl, swiperInstance)
    : null;

  // Only the loop rotates the wrapper, so only the loop can trap the focus.
  const loopFocusCleanup = sliderSettings.loop
    ? initSliderLoopFocus(sliderEl, swiperInstance)
    : null;

  // When a product card contains its own media slider (.product-card__media--slider),
  // hovering it should freeze the section slider so the two swipers don't compete
  // for touch-move and mousewheel events.
  //let productCardCleanup = null;
  //const productCardSliders = [
  //  ...sliderEl.querySelectorAll(".product-card__media--slider"),
  //];
  //if (productCardSliders.length > 0) {
  //  const onCardEnter = () => {
  //    swiperInstance.allowTouchMove = false;
  //    if (swiperInstance.mousewheel) swiperInstance.mousewheel.disable();
  //  };
  //  const onCardLeave = () => {
  //    swiperInstance.allowTouchMove = true;
  //    if (swiperInstance.mousewheel) swiperInstance.mousewheel.enable();
  //  };
  //  productCardSliders.forEach((card) => {
  //    card.addEventListener("mouseenter", onCardEnter);
  //    card.addEventListener("mouseleave", onCardLeave);
  //  });
  //  productCardCleanup = () => {
  //    productCardSliders.forEach((card) => {
  //      card.removeEventListener("mouseenter", onCardEnter);
  //      card.removeEventListener("mouseleave", onCardLeave);
  //    });
  //  };
  //}

  return () => {
    if (autoplayObserver) autoplayObserver.disconnect();
    if (typeof cursorCleanup === "function") cursorCleanup();
    if (typeof loopFocusCleanup === "function") loopFocusCleanup();
    //if (typeof productCardCleanup === "function") productCardCleanup();
    if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);

    // destroy() keeps the a11y slide roles and live region, which would still
    // announce the grid as a carousel.
    if (isMediaSlider) {
      sliderEl.classList.remove("swiper", "swiper-backface-hidden");
      if (wrapperEl) {
        wrapperEl.classList.remove("swiper-wrapper");
        wrapperEl.removeAttribute("id");
        wrapperEl.removeAttribute("aria-live");
        [...wrapperEl.children].forEach((el) => {
          el.classList.remove("swiper-slide");
          el.removeAttribute("role");
          el.removeAttribute("aria-label");
          el.removeAttribute("aria-roledescription");
        });
      }
    }
  };
}

document.addEventListener("DOMContentLoaded", () => {
  const sectionsWithCardSlider = document.querySelectorAll(
    "[data-section-has-card-slider='true']"
  );
  sectionsWithCardSlider.forEach(initSectionSliderLayout);
});

document.addEventListener("shopify:section:load", function (event) {
  if (typeof event.target?.querySelector !== "function") return;

  const section = event.target?.querySelector(
    "[data-section-has-card-slider='true']"
  );
  if (section) initSectionSliderLayout(section);
});

document.addEventListener("shopify:section:unload", function (event) {
  if (typeof event.target?.querySelector !== "function") return;

  const section = event.target.querySelector(
    "[data-section-has-card-slider='true']"
  );
  if (section) runCardSliderCleanup(section);
});

if (navigator.userAgent.indexOf("iPhone") > -1) {
  document
    .querySelector("[name=viewport]")
    .setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1"
    );
}

function lockScrollBodyOnIos(openScrollY) {
  if (navigator.userAgent.indexOf("iPhone") > -1) {
    document.documentElement.style.setProperty(
      "--current-body-scroll",
      `${-1 * openScrollY}px`
    );
    document.documentElement.style.setProperty("scroll-behavior", "unset");
    document.body.classList.add("forced-scroll-locked");
  }
}

function unlockScrollBodyOnIos(openScrollY) {
  if (navigator.userAgent.indexOf("iPhone") > -1) {
    document.body.classList.remove("forced-scroll-locked");
    document.documentElement.scrollTop = openScrollY;
    document.documentElement.style.removeProperty("--current-body-scroll");
    document.documentElement.style.removeProperty("scroll-behavior");
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter((element) => {
    if (element.offsetParent === null) return false;
    // tabindex="-1" and inert subtrees take focus only programmatically, never from
    // Tab, so they must not become a focus-trap boundary
    if (element.matches("[tabindex^='-']")) return false;
    return element.closest("[inert]") === null;
  });
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", "false");

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-mobile-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return;

    // Recompute on every Tab — modals load content (installments, dynamic
    // checkout) after the trap is set, so a cached list goes stale.
    const elements = getFocusableElements(container);
    if (elements.length === 0) return;

    const first = elements[0];
    const last = elements[elements.length - 1];
    const target = event.target;

    if (!container.contains(target)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    // Shadow-DOM hosts (payment terms, dynamic checkout) retarget events to the
    // host, which is absent from the list — hijack Tab only at real boundaries
    // and let the browser walk everything in between; focusin covers escapes.
    if (event.shiftKey) {
      if (target === first || target === container) {
        event.preventDefault();
        last.focus();
      }
    } else if (target === last || target === container) {
      event.preventDefault();
      first.focus();
    }
  };

  trapFocusHandlers.focusin = (event) => {
    if (container.contains(event.target)) return;

    const elements = getFocusableElements(container);
    elements[0]?.focus();
  };

  document.addEventListener("keydown", trapFocusHandlers.keydown, true);
  document.addEventListener("focusin", trapFocusHandlers.focusin, true);

  elementToFocus?.focus();
}

function pauseAllMedia(parentElement = document, exceptElement = null) {
  parentElement.querySelectorAll(".js-youtube").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  parentElement.querySelectorAll(".js-vimeo").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  parentElement.querySelectorAll("video").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.pause();
  });
  parentElement.querySelectorAll("product-model").forEach((model) => {
    if (exceptElement && exceptElement.contains(model)) return;
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin, true);
  document.removeEventListener("keydown", trapFocusHandlers.keydown, true);

  if (elementToFocus) elementToFocus.focus();
}

// Password modal (<details role="dialog">): Esc and a backdrop click close it and
// focus stays inside while it is open. Delegated on document, so a section
// re-render in the theme editor needs no re-binding and no teardown.
function closePasswordModal(modal) {
  modal.removeAttribute("open");
  removeTrapFocus(modal.querySelector(".password-modal__toggle"));
}

document.addEventListener("keyup", (event) => {
  if (event.code?.toUpperCase() !== "ESCAPE") return;

  const modal = document.querySelector("details.password-modal[open]");
  if (modal) closePasswordModal(modal);
});

document.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const toggle = target.closest(".password-modal__toggle");
  if (toggle) {
    const modal = toggle.closest("details.password-modal");
    if (!modal) return;
    // The native <details> toggle runs after this handler — read the state next frame.
    requestAnimationFrame(() => {
      if (modal.hasAttribute("open")) {
        trapFocus(modal, modal.querySelector("#Password"));
      } else {
        removeTrapFocus();
      }
    });
    return;
  }

  const openModal = document.querySelector("details.password-modal[open]");
  if (!openModal) return;
  if (!target.closest(".password-modal__wrapper")) return;

  // The content box stretches across the overlay (on narrow viewports it leaves no
  // side backdrop at all), so "outside the dialog" means the box itself rather than
  // one of its children: the empty area around the heading, form and footer link.
  const content = target.closest(".password-modal__content");
  if (content && content !== target) return;

  closePasswordModal(openModal);
});

function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  cents = parseInt(cents, 10);

  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || theme.moneyFormat;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ",",
    decimal = "."
  ) {
    if (isNaN(number) || number == null) {
      return "0";
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : "";

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2, ",", ".");
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0, ",", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ", ".");
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ",");
      break;
    case "amount_with_period_and_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    default:
      value = formatWithDelimiters(cents, 2, ",", ".");
  }

  return formatString.replace(placeholderRegex, value);
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

(function () {
  function copyURI(e, copyLink) {
    e.preventDefault();

    const url = window.location.href;

    const handleCopy = (btn, success) => {
      if (success) {
        btn.classList.add("copied");
        setTimeout(() => btn.classList.remove("copied"), 1000);
      } else {
        btn.classList.add("notCopied");
        setTimeout(() => btn.classList.remove("notCopied"), 1000);
      }
    };

    // Modern Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => handleCopy(copyLink, true))
        .catch(() => handleCopy(copyLink, false));
    } else {
      // Fallback for unsupported browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      textArea.style.position = "fixed"; // avoid scrolling
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand("copy");
        handleCopy(copyLink, successful);
      } catch (err) {
        handleCopy(copyLink, false);
      }

      document.body.removeChild(textArea);
    }
  }

  const copyLinks = document.querySelectorAll(".copy-btn");
  copyLinks.forEach((copyLink) =>
    copyLink.addEventListener("click", (e) => copyURI(e, copyLink))
  );
})();

if (!Element.prototype.replaceChildren) {
  Element.prototype.replaceChildren = function (...nodes) {
    while (this.firstChild) this.removeChild(this.firstChild);
    this.append(...nodes);
  };
}

// [Theme custom event] - Cart UI refresh event
document.addEventListener("cart:refresh", (event) => {
  const sectionsToUpdate = [
    {
      id: "cart-icon-bubble",
      selector: ".cart-count-bubble",
    },
    {
      id: "cart-drawer",
      selector: "#CartDrawer",
    },
    {
      id: "main-cart",
      selector: ".main-cart",
    },
  ];

  const sectionIds = sectionsToUpdate.map((section) => section.id).join(",");

  fetch(`${routes.cart_url}?sections=${sectionIds}`)
    .then((response) => response.json())
    .then((sections) => {
      sectionsToUpdate.forEach((section) => {
        const sectionHTML = sections[section.id];
        if (!sectionHTML) return;

        const html = new DOMParser().parseFromString(sectionHTML, "text/html");
        const selector = section.selector;

        const sourceElement = html.querySelector(selector);
        const destinationElements = document.querySelectorAll(selector);

        if (sourceElement && destinationElements.length > 0) {
          destinationElements.forEach((destinationElement) => {
            destinationElement.innerHTML = sourceElement.innerHTML;
            if (sourceElement.classList.contains("is-empty")) {
              destinationElement.classList.add("is-empty");
            } else {
              destinationElement.classList.remove("is-empty");
            }
          });
        }
      });
    })
    .catch((error) => {
      console.error("Error updating cart:", error);
    });
});

/*
 * ------------ Theme Web Components ------------
 */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.changeEvent = new Event("change", { bubbles: true });
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.input = this.querySelector("input");
    this.isCartItem = Boolean(this.closest(".cart-item"));

    this.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", this.onButtonClick.bind(this));
    });

    this.setMinimumDisable();

    var eventList = ["paste", "input"];
    for (const event of eventList) {
      this.input.addEventListener(event, (e) => {
        const value = e.currentTarget.value;
        const numberRegex = this.isCartItem ? /^\d*$/ : /^0*?[1-9]\d*$/;

        if (numberRegex.test(value) || value === "") {
        } else {
          e.currentTarget.value = 1;
        }

        this.setMinimumDisable();
      });
    }

    this.input.addEventListener("focusout", (e) => {
      if (e.currentTarget.value === "") {
        e.currentTarget.value = 1;
        this.setMinimumDisable();
      }
    });
  }

  setMinimumDisable() {
    if (!this.input) return;
    const value = Number(this.input.value);

    const minusBtn = this.querySelector('button[name="minus"]');
    if (minusBtn) {
      const minValue = this.input.min === "" ? 1 : Number(this.input.min);
      minusBtn.classList.toggle("disabled", value <= minValue);
    }

    const plusBtn = this.querySelector('button[name="plus"]');
    if (plusBtn) {
      const hasMax = this.input.max !== "";
      plusBtn.classList.toggle(
        "disabled",
        hasMax && value >= Number(this.input.max)
      );
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

    this.setMinimumDisable();
  }
}

if (!customElements.get("quantity-input")) {
  customElements.define("quantity-input", QuantityInput);
}

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetBtn = this.querySelector(".js-search-input-reset");

    this.input?.form?.addEventListener("reset", this.onFormReset.bind(this));
    this.input?.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.resetBtn?.addEventListener("click", this.onFormReset.bind(this));
  }

  connectedCallback() {
    this.toggleResetBtn(this.input?.value);
  }

  toggleResetBtn(value) {
    if (!this.resetBtn) return;

    if (value) {
      this.resetBtn.style.display = "flex";
      this.resetBtn.classList.add("active");
    } else {
      this.resetBtn.style.display = "none";
      this.resetBtn.classList.remove("active");
    }
  }

  onChange(event) {
    this.toggleResetBtn(event.target.value);
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();

    if (this.input) {
      if (this.closest(".search-section") && window.location.search) {
        const params = new URLSearchParams(window.location.search);
        const q = params.get("q");
        if (this.input.value !== q) {
          setTimeout(() => {
            this.input.value = q;
            this.input.focus();
            this.toggleResetBtn(this.input.value);
          }, 0);
        } else {
          const cleanURL = window.location.pathname;
          window.location.href = cleanURL;
        }
      } else {
        this.input.value = "";
        this.input.focus();
        this.toggleResetBtn("");
      }
    }
  }
}

if (!customElements.get("search-form")) {
  customElements.define("search-form", SearchForm);
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");
    this.closeBtn = this.querySelector(".modal-close-button");
    const summaryElements = this.querySelectorAll("summary");
    this.addAccessibilityAttributes(summaryElements);

    this.headerWrapper = document.querySelector(".header-wrapper");
    if (this.headerWrapper) this.headerWrapper.preventHide = false;

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );

    this.closeBtn?.addEventListener(
      "click",
      this.onCloseButtonClick.bind(this)
    );
  }

  addAccessibilityAttributes(summaryElements) {
    summaryElements.forEach((element) => {
      element.setAttribute("role", "button");
      element.setAttribute("aria-expanded", false);
      element.setAttribute("aria-controls", element.nextElementSibling.id);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute("open");

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );

      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
      });
    }
  }

  openMenuDrawer(summaryElement) {
    if (this.headerWrapper) this.headerWrapper.preventHide = true;
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-drawer`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event !== undefined) {
      this.mainDetailsToggle.classList.remove("menu-opening");
      this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
      });
      this.mainDetailsToggle
        .querySelector("summary")
        .setAttribute("aria-expanded", false);
      document.body.classList.remove(`overflow-hidden-drawer`);
      removeTrapFocus(elementToFocus);
      this.closeAnimation(this.mainDetailsToggle);
      if (this.headerWrapper) this.headerWrapper.preventHide = false;
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    if (!detailsElement) return;
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement?.classList.remove("menu-opening");
    removeTrapFocus();
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

if (!customElements.get("menu-drawer")) {
  customElements.define("menu-drawer", MenuDrawer);
}

class HeaderMobileDrawer extends MenuDrawer {
  constructor() {
    super();
    this.header = document.querySelector(".shopify-section-header");
    this.headerWrapper = document.querySelector(".header-wrapper");
    if (this.headerWrapper) this.headerWrapper.preventHide = false;
    this.openBtn = this.querySelector(".js-menu-drawer-open");
    this.closeBtn = this.querySelector(".js-menu-drawer-close");
    this.isOpen = false;

    this.closeBtn?.addEventListener("click", () => {
      this.closeMenuDrawer(this.openBtn);
    });
  }

  openMenuDrawer(summaryElement) {
    if (this.headerWrapper) this.headerWrapper.preventHide = true;
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add("overflow-hidden-mobile-menu");
    this.isOpen = true;
    this.openScrollY = window.scrollY;

    // styles for forced disable body scroll on iOS
    if (typeof window.lockScrollBodyOnIos === "function") {
      this.openScrollY = window.scrollY;
      window.lockScrollBodyOnIos(this.openScrollY);
    }
  }

  closeMenuDrawer(elementToFocus) {
    if (!elementToFocus) return;
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    document.body.classList.remove("overflow-hidden-mobile-menu");
    document.body.classList.remove("overflow-hidden");
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    // close mega menus mobile if open
    const megaSubmenuLinks = this.querySelectorAll(
      ".header-mobile-menu__item--megasubmenu"
    );
    if (megaSubmenuLinks && megaSubmenuLinks.length) {
      megaSubmenuLinks.forEach((link) => {
        const megaSubmenuEl = link.querySelector(".mega-submenu-mobile");
        if (!megaSubmenuEl) return;
        megaSubmenuEl.classList.remove("mega-submenu-mobile--visible");
      });
    }

    // close search modal if open
    const searchModal = document.querySelector(".header-search-modal");
    if (searchModal && searchModal.getAttribute("open")) {
      const headerSearch = document.querySelector("header-search");
      if (headerSearch && typeof headerSearch.close === "function") {
        headerSearch.close(null, elementToFocus);
      }
    }

    // styles for forced disable body scroll on iOS
    if (typeof window.unlockScrollBodyOnIos === "function") {
      window.unlockScrollBodyOnIos(this.openScrollY);
    }

    if (this.headerWrapper) this.headerWrapper.preventHide = false;
    this.isOpen = false;
  }
}

if (!customElements.get("header-mobile-drawer")) {
  customElements.define("header-mobile-drawer", HeaderMobileDrawer);
}

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll(
      '[id^="ModalCloseBtn-"], [id^="ModalCloseOverlay-"]'
    ).forEach((el) => el.addEventListener("click", () => this.hide(false)));

    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        ) {
          this.hide();
        }
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    this.addEventListener(
      "transitionend",
      () => {
        if (this.hasAttribute("open")) {
          trapFocus(this, this.querySelector('[role="dialog"]'));
        }
      },
      { once: true }
    );
  }

  hide() {
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    document.body.classList.remove("overflow-hidden");
  }
}

if (!customElements.get("modal-dialog")) {
  customElements.define("modal-dialog", ModalDialog);
}

class ProductModal extends ModalDialog {
  constructor() {
    super();
  }
}

if (!customElements.get("product-modal")) {
  customElements.define("product-modal", ProductModal);
}

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}

if (!customElements.get("modal-opener")) {
  customElements.define("modal-opener", ModalOpener);
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (poster) {
      poster.addEventListener("click", this.loadContent.bind(this));
    }

    if (this.dataset.autoplay === "true") {
      this.loadContent();
    }
  }

  loadContent() {
    // Outside a product gallery (e.g. a product-compare card) only the own section is paused,
    // so a background video elsewhere on the page keeps playing.
    const parentEl =
      this.closest(".product__media") ||
      this.closest(".product-media-modal") ||
      this.closest(".shopify-section") ||
      document;
    pauseAllMedia(parentEl);

    if (this.getAttribute("loaded")) return;

    const template = this.querySelector("template");
    if (!template) return;

    const content = document.createElement("div");
    content.appendChild(template.content.firstElementChild.cloneNode(true));
    this.setAttribute("loaded", true);

    const media = content.querySelector("video, model-viewer, iframe");
    if (!media) return;

    const deferredElement = this.appendChild(media);

    if (["VIDEO", "IFRAME"].includes(deferredElement.nodeName)) {
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            let shouldAutoplay = false;
            const parentSlide = entry.target.closest(".swiper-slide");
            if (parentSlide) {
              if (parentSlide.classList.contains("swiper-slide-active")) {
                shouldAutoplay = true;
              }
            } else {
              if (entry.intersectionRatio > 0.7) {
                shouldAutoplay = true;
              }
            }

            if (shouldAutoplay) {
              if (
                (this.dataset.autoplay === "true" &&
                  !this.closest("product-modal")) ||
                (this.dataset.autoplay === "true" &&
                  this.closest("product-modal")?.hasAttribute("open"))
              ) {
                pauseAllMedia(parentEl, this);

                if (entry.target.nodeName === "VIDEO") {
                  entry.target.play()?.catch((error) => {
                    console.error("Error playing video:", error);
                  });
                } else if (
                  entry.target.nodeName === "IFRAME" &&
                  entry.target.contentWindow
                ) {
                  if (entry.target.classList.contains("js-youtube")) {
                    entry.target.contentWindow.postMessage(
                      '{"event":"command","func":"playVideo","args":""}',
                      "*"
                    );
                  } else {
                    entry.target.contentWindow.postMessage(
                      '{"method":"play"}',
                      "*"
                    );
                  }
                }
              }
            } else {
              if (entry.target.nodeName === "VIDEO") {
                entry.target.pause();
              } else if (
                entry.target.nodeName === "IFRAME" &&
                entry.target.contentWindow
              ) {
                if (entry.target.classList.contains("js-youtube")) {
                  entry.target.contentWindow.postMessage(
                    '{"event":"command","func":"pauseVideo","args":""}',
                    "*"
                  );
                } else {
                  entry.target.contentWindow.postMessage(
                    '{"method":"pause"}',
                    "*"
                  );
                }
              }
            }
          });
        },
        {
          threshold: Array.from({ length: 1000 }, (_, i) => i / 1000),
        }
      );

      this.videoObserver.observe(deferredElement);
    }

    const modelViewer = this.closest(".swiper-slide-active")?.querySelector(
      "model-viewer"
    );
    if (
      modelViewer &&
      !modelViewer.classList.contains("shopify-model-viewer-ui__disabled")
    ) {
      const swiper = this.closest(".swiper")?.swiper;
      if (swiper) {
        swiper.params.noSwiping = true;
        swiper.params.noSwipingClass = "swiper-slide";
      }
    }

    this.querySelectorAll(
      "video, .shopify-model-viewer-ui__controls-overlay"
    ).forEach((media) => {
      if (this.getAttribute("loaded") === "true") {
        media.addEventListener("click", (event) => {
          pauseAllMedia(parentEl, this);
        });
      }
    });
  }

  disconnectedCallback() {
    this.videoObserver?.disconnect();
  }
}

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", DeferredMedia);
}

class ProductModel extends DeferredMedia {
  constructor() {
    super();
  }

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  setupModelViewerUI(errors) {
    if (errors) return;

    this.modelViewerUI = new Shopify.ModelViewerUI(
      this.querySelector("model-viewer")
    );

    const $this = this;
    const target = $this.querySelector("model-viewer");

    this.modelViewerObserver = new MutationObserver((records) => {
      if ($this.closest(".swiper") && $this.closest(".swiper").swiper) {
        const swiperObj = $this.closest(".swiper").swiper;
        if (!target.classList.contains("shopify-model-viewer-ui__disabled")) {
          swiperObj.params.noSwiping = true;
          swiperObj.params.noSwipingClass = "swiper-slide";
        } else {
          swiperObj.params.noSwiping = false;
        }
      }
    });

    this.modelViewerObserver.observe(target, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.modelViewerObserver?.disconnect();
  }
}

if (!customElements.get("product-model")) {
  customElements.define("product-model", ProductModel);
}

window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([
      {
        name: "shopify-xr",
        version: "1.0",
        onLoad: this.setupShopifyXR.bind(this),
      },
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", () =>
        this.setupShopifyXR()
      );
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  },
};

window.addEventListener("DOMContentLoaded", () => {
  window.ProductModel?.loadShopifyXR();
});

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.addEventListener("change", this.onVariantChange.bind(this));

    this.variantMediaDisplay =
      this.closest(".product")?.querySelector(".product__media")?.dataset
        ?.variantMediaDisplay || "show_all";
  }

  onVariantChange(event) {
    if (!this.contains(event.target)) return;

    const selectedValuesIds = this.getSelectedValuesIds();
    const requestUrl = this.createRequestUrl({
      selectedValuesIds: selectedValuesIds,
      connectedProductURL: this.connectedProductURL,
    });

    this.fetchProductInfo(requestUrl);

    this.resetErrorMessage();
  }

  getSelectedValuesIds() {
    const controls = [...this.querySelectorAll(".product-form__controls")];

    controls.sort((a, b) => {
      return (
        Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
      );
    });

    return controls.map((control) => {
      const checkedInput = control.querySelector('input[type="radio"]:checked');

      // Supporting combined listings: If the checked input has a connected product url, save it to use in the request
      if (checkedInput.dataset?.connectedProductUrl) {
        this.connectedProductURL = checkedInput.dataset.connectedProductUrl;
      }
      return checkedInput?.dataset?.optionValueId
        ? checkedInput.dataset.optionValueId
        : null;
    });
  }

  createRequestUrl({ selectedValuesIds = [], connectedProductURL = "" }) {
    const productUrl = connectedProductURL || `${this.dataset.url}`;
    const sectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    if (selectedValuesIds.length) {
      const params = [];
      params.push(`section_id=${sectionId}`);
      params.push(`option_values=${selectedValuesIds.join(",")}`);
      return `${productUrl}?${params.join("&")}`;
    }
  }

  fetchProductInfo(requestUrl) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const requestController = this.abortController;

    this.toggleButtonsLoading(true);

    fetch(requestUrl, { signal: requestController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");

        try {
          // Search this.currentVariant
          this.setCurrentVariantAfterFetch(html);

          this.updateURL();
          this.updateVariantInput();
          this.updatePickupAvailability();

          if (
            this.currentVariant.featured_media &&
            this.variantMediaDisplay === "show_all"
          ) {
            // If variant display != "show_all", the media gallery element is fully replaced inside updateElementsAfterFetch
            const mediaId = `${this.dataset.section}-${this.currentVariant.featured_media.id}`;
            this.updateMedia(mediaId);
          }
        } catch (err) {}

        this.updateElementsAfterFetch(html);

        if (!this.currentVariant) {
          this.setUnavailable();
        } else {
          this.toggleAddButton(
            !this.currentVariant.available,
            window.variantStrings.soldOut
          );
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.info("Fetch aborted by user");
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        // Only the latest request may clear the loading state.
        if (this.abortController === requestController) {
          this.toggleButtonsLoading(false);
        }
      });
  }

  setCurrentVariantAfterFetch(html) {
    // attr data-original-section use for Quick view modal
    const sourceSectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    const variantPickerSource = html.querySelector(
      `[data-id="VariantPicker-${sourceSectionId}"]`
    );
    if (!variantPickerSource) return;

    const newVariantDataEl = variantPickerSource.querySelector(
      "[data-selected-variant]"
    );
    if (!newVariantDataEl) return;

    const newVariantData = variantPickerSource.querySelector(
      "[data-selected-variant]"
    ).innerHTML;

    const selectedVariant = !!newVariantData
      ? JSON.parse(newVariantData)
      : null;

    this.currentVariant = selectedVariant;
  }

  updateURL() {
    if (this.dataset.updateUrl === "false") return;
    const newUrl = this.currentVariant
      ? `${this.dataset.url}?variant=${this.currentVariant.id}`
      : this.dataset.url;

    window.history.replaceState({}, "", newUrl);
  }

  updateVariantInput() {
    if (!this.currentVariant?.id) return;

    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));

      // Forms without a visible quantity selector (sticky bar / hidden
      // selector) carry a hidden quantity input — keep it at the variant's
      // minimum orderable quantity so add to cart passes the quantity rules.
      // No quantity_rule in the variant JSON -> keep the server-rendered value
      const hiddenQuantityInput = productForm.querySelector(
        'input[type="hidden"][name="quantity"]'
      );
      const quantityRule = this.currentVariant.quantity_rule;
      if (hiddenQuantityInput && quantityRule) {
        hiddenQuantityInput.value = Math.max(
          quantityRule.min || 1,
          quantityRule.increment || 1
        );
      }
    });

    publish(PUB_SUB_EVENTS.variantChange, {
      data: {
        sectionId: this.dataset.section,
        variant: this.currentVariant,
      },
    });

    // [Start theme custom event] - Variant change event
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        detail: {
          variant: this.currentVariant,
          previousVariant: this.previousVariant || null,
          formElement: this,
          sectionId: this.dataset.section,
        },
      })
    );

    this.previousVariant = this.currentVariant;
    // [End theme custom event] - Variant change event
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelectorAll(
      `pickup-availability[data-section="${this.dataset.section}"]`
    );
    if (!pickUpAvailability.length) return;
    pickUpAvailability.forEach((el) => {
      if (this.currentVariant && this.currentVariant.available) {
        el.fetchAvailability(this.currentVariant.id);
      } else {
        el.removeAttribute("available");
        el.innerHTML = "";
      }
    });
  }

  updateMedia(mediaId) {
    if (!mediaId) return;

    const mediaEl = document.querySelector(
      `[data-section="product-media-${this.dataset.section}"]`
    );
    if (!mediaEl) return;

    // for slider and slider_previews layout
    const sliderEl = mediaEl.querySelector(".js-media-list");
    if (sliderEl && sliderEl.swiper) {
      const slideCount = sliderEl.querySelectorAll(".swiper-slide").length;
      const speed = slideCount > 32 ? 0 : 800;
      const swiperSlideIndex = sliderEl.swiper.slides.findIndex(
        (slide) => slide.dataset?.mediaId === mediaId
      );
      if (swiperSlideIndex !== -1) {
        sliderEl.swiper.slideTo(swiperSlideIndex, speed);
      }
    }

    // for stacked and grid layout
    const stackedOrGridEl =
      mediaEl.querySelector(
        ".product__media-list[data-desktop-type='stacked']"
      ) ||
      mediaEl.querySelector(
        ".product__media-list[data-desktop-type='stacked_previews']"
      ) ||
      mediaEl.querySelector(".product__media-list[data-desktop-type='grid']") ||
      mediaEl.querySelector(".product__media-list[data-mobile-type='grid']");
    if (stackedOrGridEl) {
      stackedOrGridEl
        .querySelectorAll("[data-media-id].product__media-item")
        .forEach((mediaItem) => {
          const mediaItemId = mediaItem.dataset?.mediaId;
          if (mediaItemId === mediaId) {
            mediaItem.classList.add("stacked-or-grid-active");
          } else {
            mediaItem.classList.remove("stacked-or-grid-active");
          }
        });
    }
  }

  updateElementsAfterFetch(html) {
    // attr data-original-section use for Quick view modal
    const currentSectionId = this.dataset.section;
    const sourceSectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    // Price
    const priceSource = html.querySelector(
      `[data-id="Price-${sourceSectionId}"]`
    );
    const priceDestination = document.querySelectorAll(
      `[data-id="Price-${currentSectionId}"]`
    );
    if (priceSource) {
      priceDestination.forEach((el) => {
        el.innerHTML = priceSource.innerHTML;
        el.classList.remove("visibility-hidden");
      });
    }

    // Price in sticky add to cart bar
    const priceStickySource = html.querySelector(
      `[data-id="PriceSticky-${sourceSectionId}"]`
    );
    const priceStickyDestination = document.querySelectorAll(
      `[data-id="PriceSticky-${currentSectionId}"]`
    );
    if (priceStickySource) {
      priceStickyDestination.forEach((el) => {
        el.innerHTML = priceStickySource.innerHTML;
        el.classList.remove("visibility-hidden");
      });
    }

    // Badges (sale percentage and sold-out state differ per variant)
    const badgesSource = html.querySelector(
      `[data-id="Badges-${sourceSectionId}"]`
    );
    const badgesDestination = document.querySelectorAll(
      `[data-id="Badges-${currentSectionId}"]`
    );
    if (badgesSource) {
      badgesDestination.forEach((el) => {
        el.innerHTML = badgesSource.innerHTML;
        el.classList.remove("visibility-hidden");
      });
    }

    // Inventory
    const inventorySource = html.querySelector(
      `[data-id="Inventory-${sourceSectionId}"]`
    );
    const inventoryDestination = document.querySelectorAll(
      `[data-id="Inventory-${currentSectionId}"]`
    );
    if (inventorySource) {
      inventoryDestination.forEach((el) => {
        el.innerHTML = inventorySource.innerHTML;
        el.classList.toggle(
          "visibility-hidden",
          inventorySource.innerText === ""
        );
      });
    }

    // SKU
    const skuSource = html.querySelector(`[data-id="Sku-${sourceSectionId}"]`);
    const skuDestination = document.querySelectorAll(
      `[data-id="Sku-${currentSectionId}"]`
    );
    if (skuSource) {
      skuDestination.forEach((el) => {
        el.innerHTML = skuSource.innerHTML;
        el.classList.toggle(
          "visibility-hidden",
          skuSource.classList.contains("visibility-hidden")
        );
      });
    }

    // Media gallery badges — sale/compare-at can differ per variant, so they are
    // refreshed on EVERY variant change (not only for combined listings).
    const mediaBadgesSrc =
      html.querySelector(
        `[data-section="product-media-${sourceSectionId}"] .product__media-badges`
      ) || html.querySelector(".product__media-badges");
    const mediaBadgesDest = document.querySelectorAll(
      `[data-section="product-media-${currentSectionId}"] .product__media-badges`
    );
    if (mediaBadgesSrc && mediaBadgesDest.length) {
      mediaBadgesDest.forEach((el) => {
        el.innerHTML = mediaBadgesSrc.innerHTML;
        el.classList.remove("visibility-hidden");
      });
    }

    // Variant picker: Color swatches label
    const colorNameSources = html.querySelectorAll(
      `[data-id^="ColorName-${sourceSectionId}"]`
    );
    const colorNameDestinations = document.querySelectorAll(
      `[data-id^="ColorName-${currentSectionId}"]`
    );
    if (colorNameSources?.length === colorNameDestinations?.length) {
      colorNameDestinations.forEach((colorNameDestination, index) => {
        colorNameDestination.classList.toggle(
          "visibility-hidden",
          colorNameSources[index].classList.contains("visibility-hidden")
        );
        colorNameDestination.innerHTML = colorNameSources[index].innerHTML;
      });
    }

    // Variant picker: Options
    const variantPickerSource = html.querySelector(
      `[data-id="VariantPicker-${sourceSectionId}"]`
    );
    const variantPickerDestinations = document.querySelectorAll(
      `[data-id="VariantPicker-${currentSectionId}"]`
    );
    if (variantPickerSource) {
      const quickViewModal = this.closest("quick-view-modal");
      const sourceOptionValues = variantPickerSource.querySelectorAll(
        `[data-id^="OptionValues-${sourceSectionId}-"]`
      );
      const sourceCurrentVariantScript = variantPickerSource.querySelector(
        "[data-selected-variant]"
      );

      if (sourceOptionValues.length && !this.connectedProductURL) {
        variantPickerDestinations.forEach((pickerDestEl) => {
          sourceOptionValues.forEach((sourceOptionsEl) => {
            const position = sourceOptionsEl.dataset.optionPosition;
            if (!position) return;
            const destOptionsEl = pickerDestEl.querySelector(
              `[data-option-position="${position}"][data-id^="OptionValues-"]`
            );
            if (destOptionsEl) {
              destOptionsEl.innerHTML = quickViewModal
                ? sourceOptionsEl.innerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : sourceOptionsEl.innerHTML;
            }
          });

          if (sourceCurrentVariantScript) {
            const destCurrentVariantScript = pickerDestEl.querySelector(
              "[data-selected-variant]"
            );
            if (destCurrentVariantScript) {
              destCurrentVariantScript.innerHTML =
                sourceCurrentVariantScript.innerHTML;
            }
          }
        });
      } else {
        // fallback to replace whole variant picker html
        if (quickViewModal) {
          variantPickerDestinations.forEach((el) => {
            el.innerHTML = variantPickerSource.innerHTML.replaceAll(
              sourceSectionId,
              `quickview-${sourceSectionId}`
            );
          });
        } else {
          variantPickerDestinations.forEach((el) => {
            el.innerHTML = variantPickerSource.innerHTML;
          });
        }
      }
    }

    // Update elements for B2B quantity pricing https://help.shopify.com/en/manual/b2b/catalogs/quantity-pricing
    const productButtonsSource = html.querySelector(
      `[data-id="ProductButtons-${sourceSectionId}"]`
    );
    const productButtonsDestination = document.querySelectorAll(
      `[data-id="ProductButtons-${currentSectionId}"]`
    );
    if (productButtonsSource) {
      const quickViewModal = this.closest("quick-view-modal");
      const sourceButtonsWrapper = productButtonsSource.querySelector(
        ".product-form__buttons"
      );
      productButtonsDestination.forEach((el) => {
        const buttonsWrapper = el.querySelector(".product-form__buttons");

        if (sourceButtonsWrapper && buttonsWrapper) {
          const sourceHasQuantityPricing =
            sourceButtonsWrapper.dataset.hasQuantityPricing === "true";
          const destHadQuantityPricing =
            buttonsWrapper.dataset.hasQuantityPricing === "true";

          // This function is executed if quantity pricing is configured for the product or product variant
          if (sourceHasQuantityPricing || destHadQuantityPricing) {
            if (sourceHasQuantityPricing) {
              buttonsWrapper.dataset.hasQuantityPricing = "true";
            } else {
              buttonsWrapper.removeAttribute("data-has-quantity-pricing");
            }

            // Update b2b elements (volume pricing + quantity rules)
            const sourceB2bEl = sourceButtonsWrapper.querySelector(
              ".product-b2b-elements"
            );
            const destB2bEl = buttonsWrapper.querySelector(
              ".product-b2b-elements"
            );
            if (sourceB2bEl && destB2bEl) {
              destB2bEl.innerHTML = quickViewModal
                ? sourceB2bEl.innerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : sourceB2bEl.innerHTML;
            } else if (sourceB2bEl && !destB2bEl) {
              const b2bHTML = quickViewModal
                ? sourceB2bEl.outerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : sourceB2bEl.outerHTML;
              buttonsWrapper.insertAdjacentHTML("afterbegin", b2bHTML);
            } else if (!sourceB2bEl && destB2bEl) {
              destB2bEl.remove();
            }

            // Update quantity input
            const sourceQuantityWrapper = sourceButtonsWrapper.querySelector(
              ".product-form__quantity"
            );
            const destQuantityWrapper = buttonsWrapper.querySelector(
              ".product-form__quantity"
            );
            if (sourceQuantityWrapper && destQuantityWrapper) {
              destQuantityWrapper.innerHTML = quickViewModal
                ? sourceQuantityWrapper.innerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : sourceQuantityWrapper.innerHTML;
            }
          }
        }
      });
    }

    // Product media
    // Product media is replaced with DOM changes and re-initialization of initProductMediaGallery only when Variant media display is not set to "Show all" or when a product is linked to the variant (e.g., via Combined listings).
    // In most other cases, it updates via updateMedia without DOM changes.
    if (this.variantMediaDisplay !== "show_all" || this.connectedProductURL) {
      const mediaSource = html.querySelector(
        `[data-section="product-media-${sourceSectionId}"]`
      );
      const mediaDestination = document.querySelector(
        `[data-section="product-media-${currentSectionId}"]`
      );
      if (mediaSource && mediaDestination) {
        mediaDestination.innerHTML = mediaSource.innerHTML;

        const parentQuickView = this.closest("quick-view-modal");
        if (parentQuickView) {
          if (typeof parentQuickView.removeDOMElements === "function") {
            parentQuickView.removeDOMElements(mediaDestination);
          }
          if (typeof parentQuickView.initSlider === "function") {
            parentQuickView.initSlider();
          }
        } else {
          // Update zoom modal for Combined listings that was moved to body by ModalDialog.connectedCallback
          if (this.connectedProductURL) {
            const zoomModalSource = html.querySelector(
              `#ProductModal-${sourceSectionId}`
            );
            const zoomModalDest = document.querySelector(
              `#ProductModal-${currentSectionId}`
            );

            if (zoomModalSource && zoomModalDest) {
              const newZoomModal = zoomModalSource.cloneNode(true);
              newZoomModal.id = `ProductModal-${currentSectionId}`;
              zoomModalDest.replaceWith(newZoomModal);
            }
          }

          const section = document.getElementById(
            `shopify-section-${currentSectionId}`
          );

          if (section && typeof window.initProductMediaGallery === "function") {
            window.initProductMediaGallery(section);
          }
        }
      }
    }

    // Replace other elements for Combined listings
    if (this.connectedProductURL) {
      // ...Src - source elements from response HTML
      // ...Dest - destination elements in the current DOM
      const productInfoSrc = html.getElementById(
        `ProductInfo-${sourceSectionId}`
      );
      const productInfoDest = document.getElementById(
        `ProductInfo-${currentSectionId}`
      );
      if (productInfoSrc && productInfoDest) {
        // Title
        const titleSrc = productInfoSrc.querySelector(".product__title");
        const titleDest = productInfoDest.querySelectorAll(".product__title");
        if (titleSrc) {
          titleDest.forEach((el) => {
            el.innerHTML = titleSrc.innerHTML;
          });
        }

        // Vendor
        const vendorSrc = productInfoSrc.querySelector(".product__vendor");
        const vendorDest = productInfoDest.querySelectorAll(".product__vendor");
        if (vendorSrc) {
          vendorDest.forEach((el) => {
            el.innerHTML = vendorSrc.innerHTML;
          });
        }

        // Description
        const descriptionSrc = productInfoSrc.querySelector(
          ".product__description"
        );
        const descriptionDest = productInfoDest.querySelectorAll(
          ".product__description"
        );
        if (descriptionSrc) {
          descriptionDest.forEach((el) => {
            el.innerHTML = descriptionSrc.innerHTML;
            el.classList.toggle(
              "hidden",
              descriptionSrc.classList.contains("hidden")
            );
          });
        }
      }

      // Complementary products
      const complementaryProductsSrc = productInfoSrc.querySelector(
        "product-recommendations.complementary-products"
      );
      const complementaryProductsDest = productInfoDest.querySelectorAll(
        "product-recommendations.complementary-products"
      );
      if (complementaryProductsSrc) {
        complementaryProductsDest.forEach((el) => {
          el.replaceWith(complementaryProductsSrc.cloneNode(true));
        });
      }
    }
  }

  toggleButtonsLoading(loading) {
    // Document-wide lookup: in the quick view modal the buy-buttons block is
    // moved outside the .product element (pinned modal footer).
    const buttonsWrapper = document.querySelectorAll(
      `[data-id="ProductButtons-${this.dataset.section}"]`
    );
    buttonsWrapper.forEach((el) => {
      el.classList.toggle("loading", loading);
    });
  }

  toggleAddButton(disable = true, text) {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const addButton = productForm.querySelector('[name="add"]');
      if (!addButton) return;

      // The sticky bar button comes from the shared button snippet, so its label
      // has no js-change-button-text hook — fall back to the button label span.
      const addButtonText =
        addButton.querySelector(".js-change-button-text") ||
        addButton.querySelector(".button__label");

      if (disable) {
        addButton.setAttribute("disabled", true);
        addButton.setAttribute("aria-disabled", true);
        if (text && addButtonText) {
          addButtonText.textContent = text;
          if (addButtonText.dataset.label) addButtonText.dataset.label = text;

          if (text === window.variantStrings.unavailable) {
            addButton.dataset.status = "unavailable";
          } else {
            addButton.dataset.status = "sold-out";
          }
        }
      } else {
        addButton.removeAttribute("disabled");
        addButton.removeAttribute("aria-disabled");
        if (addButtonText) {
          addButtonText.textContent = window.variantStrings.addToCart;
          if (addButtonText.dataset.label) {
            addButtonText.dataset.label = window.variantStrings.addToCart;
          }
        }
        addButton.dataset.status = "available";
      }
    });
  }

  setUnavailable() {
    const price = document.querySelectorAll(
      `[data-id="Price-${this.dataset.section}"]`
    );
    const priceSticky = document.querySelectorAll(
      `[data-id="PriceSticky-${this.dataset.section}"]`
    );
    const inventory = document.querySelectorAll(
      `[data-id="Inventory-${this.dataset.section}"]`
    );
    const sku = document.querySelectorAll(
      `[data-id="Sku-${this.dataset.section}"]`
    );
    const colorName = document.querySelectorAll(
      `[data-id^="ColorName-${this.dataset.section}"]`
    );
    const badges = document.querySelectorAll(
      `[data-id="Badges-${this.dataset.section}"]`
    );
    const mediaBadges = document.querySelectorAll(
      `[data-section="product-media-${this.dataset.section}"] .product__media-badges`
    );

    this.toggleAddButton(true, window.variantStrings.unavailable);

    price.forEach((el) => el.classList.add("visibility-hidden"));
    priceSticky.forEach((el) => el.classList.add("visibility-hidden"));
    inventory.forEach((el) => el.classList.add("visibility-hidden"));
    sku.forEach((el) => el.classList.add("visibility-hidden"));
    colorName.forEach((el) => el.classList.add("visibility-hidden"));
    badges.forEach((el) => el.classList.add("visibility-hidden"));
    mediaBadges.forEach((el) => el.classList.add("visibility-hidden"));
  }

  resetErrorMessage() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const parentEl =
        productForm.closest(".product-sticky-add-bar") ||
        productForm.closest("product-form");
      if (parentEl) {
        const errorWrapperEl = parentEl.querySelector(
          ".product-form__error-message-wrapper"
        );
        const errorTextEl = errorWrapperEl?.querySelector(
          ".product-form__error-message"
        );
        if (!errorWrapperEl || !errorTextEl) return;
        errorWrapperEl.setAttribute("hidden", true);
        errorTextEl.textContent = "";
      }
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}

class VariantDropdownSelect extends HTMLElement {
  constructor() {
    super();

    this.currentEl = this.querySelector(".dropdown-select__current");
    this.optionsWrapperEl = this.querySelector(".dropdown-select__options");
    this.hiddenInput = this.querySelector("input[type='hidden']");
    if (!this.currentEl || !this.optionsWrapperEl || !this.hiddenInput) return;

    this.optionsEls = Array.from(this.optionsWrapperEl.querySelectorAll("li"));
    this.inputs = Array.from(this.optionsWrapperEl.querySelectorAll("input"));
    this.hasColorSwatch = this.currentEl?.classList?.contains(
      "dropdown-select__current--with-color"
    );
    this.isActive = false;

    this.optionPosition = this.getOptionPosition();
    this._onCurrentClick = this.onCurrentClick.bind(this);
    this._onKeyUp = this.onKeyUp.bind(this);
    this._onOptionsClick = this.onClickOption.bind(this);
    this._onKeyUpOptions = this.onKeyUpOptions.bind(this);
    this._onOutsideClick = this.onOutsideClick.bind(this);
  }

  connectedCallback() {
    this.currentEl.addEventListener("click", this._onCurrentClick);
    this.currentEl.addEventListener("keyup", this._onKeyUp);
    this.currentEl.addEventListener("dblclick", this.preventDoubleClick);
    this.optionsWrapperEl.addEventListener("click", this._onOptionsClick);
    this.optionsWrapperEl.addEventListener("keyup", this._onKeyUpOptions);
    document.addEventListener("click", this._onOutsideClick);
  }

  disconnectedCallback() {
    this.currentEl.removeEventListener("click", this._onCurrentClick);
    this.currentEl.removeEventListener("keyup", this._onKeyUp);
    this.currentEl.removeEventListener("dblclick", this.preventDoubleClick);
    this.optionsWrapperEl.removeEventListener("click", this._onOptionsClick);
    this.optionsWrapperEl.removeEventListener("keyup", this._onKeyUpOptions);
    document.removeEventListener("click", this._onOutsideClick);
  }

  onCurrentClick(event) {
    event.stopPropagation();
    if (this.isActive) {
      this.onClose();
    } else {
      this.onOpen();
    }
  }

  onKeyUp(event) {
    if (event.code?.toUpperCase() === "ENTER") {
      event.preventDefault();
      event.stopPropagation();
      if (this.isActive) {
        this.onClose();
      } else {
        this.onOpen();
      }
    }
  }

  preventDoubleClick(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onClickOption(event) {
    const optionEl = event.target.closest("li");
    const currentValueEl = this.currentEl.querySelector(
      "[data-dropdown-current-value]"
    );

    if (
      !optionEl ||
      !currentValueEl ||
      !this.optionsWrapperEl.contains(optionEl)
    ) {
      event.preventDefault();
      return;
    }

    const newValue = optionEl.dataset.value;

    currentValueEl.textContent = newValue;
    this.hiddenInput.value = newValue;

    if (this.hasColorSwatch) {
      const newColor = optionEl.dataset.color;
      this.hiddenInput.dataset.colorSwatch = newColor;
      this.currentEl.style.setProperty("--swatch-color", newColor);
    }

    this.hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));

    this.inputs.forEach((el) => el.removeAttribute("checked"));
    if (event.target.tagName === "INPUT") {
      event.target.setAttribute("checked", "true");
    }

    this.onClose();
    this.currentEl.focus();
  }

  onKeyUpOptions(event) {
    if (event.code === "Escape" && this.isActive) {
      event.preventDefault();
      this.onClose();
      this.currentEl.focus();
    }
  }

  onOpen() {
    document.querySelectorAll("variant-dropdown-select").forEach((dropdown) => {
      if (dropdown !== this && typeof dropdown.onClose === "function") {
        dropdown.onClose();
      }
    });

    this.optionsWrapperEl.classList.add("active");
    this.currentEl.setAttribute("aria-expanded", "true");
    this.isActive = true;

    const currentIndex = this.inputs.findIndex((inp) =>
      inp.hasAttribute("checked")
    );
    this.inputs[currentIndex >= 0 ? currentIndex : 0]?.focus();
  }

  onClose() {
    this.optionsWrapperEl.classList.remove("active");
    this.currentEl.setAttribute("aria-expanded", "false");
    this.isActive = false;
  }

  onOutsideClick(event) {
    if (!this.isActive || this.contains(event.target)) return;
    this.onClose();
  }

  getOptionPosition() {
    const parentFieldset = this.closest(".product-form__controls");
    return Number(parentFieldset?.dataset?.optionPosition || -1);
  }

  updateCurrentOption(selectedValues = []) {
    // Method is used to synchronize after changes options in sticky bar.
    if (this.optionPosition === -1) return;
    const currentValueEl = this.currentEl.querySelector(
      "[data-dropdown-current-value]"
    );

    if (!currentValueEl || !selectedValues || !selectedValues.length) return;

    const newValue = selectedValues[this.optionPosition - 1];

    currentValueEl.textContent = newValue;
    this.hiddenInput.value = newValue;

    this.optionsEls.forEach((optionEl) => {
      const liDataValue = optionEl.dataset.value;
      if (newValue === liDataValue && this.hasColorSwatch) {
        const newColor = optionEl.dataset.color;
        this.hiddenInput.dataset.colorSwatch = newColor;
        this.currentEl.style.setProperty("--swatch-color", newColor);
      }
    });
  }
}

if (!customElements.get("variant-dropdown-select")) {
  customElements.define("variant-dropdown-select", VariantDropdownSelect);
}

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    if (this.querySelector("form")) {
      this.form = this.querySelector("form");
      this.form.querySelector("[name=id]").disabled = false;
      this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
    } else {
      if (this.querySelector("[name=id]")) {
        this.querySelector("[name=id]").disabled = false;
      }
      this.querySelector("button[type=submit]")?.addEventListener(
        "click",
        this.onSubmitHandler.bind(this)
      );
    }

    this.cartUIElement =
      this.closest("cart-drawer") ||
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");
    this.submitButton = this.querySelector('[type="submit"]');
    if (this.cartUIElement) {
      this.submitButton.setAttribute("aria-haspopup", "dialog");
    }

    this.hideErrors = this.dataset.hideErrors === "true";
  }

  // Line item property fields belong to the main product form. A second form for
  // the same product — the sticky bar — owns none of them, so on its own it
  // neither validates them nor submits what the shopper typed.
  getForeignPropertyFields() {
    const root =
      this.closest(
        ".product-section, .featured-product-section, quick-view-modal"
      ) || document;
    // Ownership is form association, not DOM position: these fields sit outside
    // any <form> and join the main one through their `form` attribute, so
    // FormData(mainForm) already carries them. Testing containment instead sent
    // every property twice from the main form.
    return Array.from(root.querySelectorAll('[name^="properties["]')).filter(
      (field) => !field.disabled && field.form !== this.form
    );
  }

  findUnmetRequiredProperty() {
    return (
      this.getForeignPropertyFields().find(
        (field) => field.required && !field.checkValidity()
      ) || null
    );
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton.getAttribute("aria-disabled") === "true") return;

    const unmetProperty = this.findUnmetRequiredProperty();
    if (unmetProperty) {
      unmetProperty.scrollIntoView({ behavior: "smooth", block: "center" });
      unmetProperty.reportValidity();
      return;
    }

    this.handleErrorMessage();

    this.submitButton.classList.add("loading");
    const btnSpinner =
      this.submitButton.querySelector(".button-loader") ||
      this.submitButton.querySelector(".loading-overlay__spinner");
    if (btnSpinner) btnSpinner.classList.remove("hidden");

    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData(this.form);
    if (!this.form) {
      formData.append("id", this.querySelector("[name=id]").value);
    }

    // Carry the property fields this form does not own, so adding from the
    // sticky bar keeps the engraving text (or the gift card recipient) the
    // shopper filled in further up the page instead of dropping it.
    this.getForeignPropertyFields().forEach((field) => {
      if (field.type === "checkbox" && !field.checked) return;
      formData.append(field.name, field.value);
    });

    // An untouched Custom property field still reaches the Cart API as a
    // property with an empty value, and Shopify treats that as a different line
    // item than the same variant with no properties at all — so one add from
    // this form and one from the sticky bar / a product card ended up as two
    // lines instead of merging. Underscore keys are Shopify's own (gift card
    // recipient flow) and are left alone.
    Array.from(new Set(formData.keys())).forEach((key) => {
      if (!key.startsWith("properties[") || key.startsWith("properties[_")) {
        return;
      }
      const value = formData.get(key);
      if (typeof value === "string" && value.trim() === "") {
        formData.delete(key);
      }
    });

    if (this.cartUIElement) {
      formData.append(
        "sections",
        this.cartUIElement
          .getSectionsToRender()
          .map((section) => section.section)
      );
      formData.append("sections_url", window.location.pathname);
    }
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        // If there is a status in the response, then this is an error
        // https://shopify.dev/docs/api/ajax/reference/cart#error-responses
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: response.description,
            message: response.message,
          });

          // [Start theme custom event] - Cart error event
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: {
                errors: response.description,
                message: response.message,
              },
            })
          );
          const refreshCartUIEvent = new CustomEvent("cart:refresh");
          document.dispatchEvent(refreshCartUIEvent);
          // [End theme custom event] - Cart error event

          this.handleErrorMessage(response.description);
          const soldOutMessage =
            this.submitButton.querySelector(".sold-out-message");
          if (!soldOutMessage) return;
          this.submitButton.setAttribute("aria-disabled", true);
          this.submitButton.querySelector("span").classList.add("hidden");
          soldOutMessage.classList.remove("hidden");
          this.error = true;
          return;
        } else if (!this.cartUIElement) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
          });
        }
        this.error = false;

        // [Start theme custom event] - Variant addition event
        document.dispatchEvent(
          new CustomEvent("variant:add", {
            detail: {
              variant: {
                id: response.variant_id || formData.get("id"),
                quantity:
                  response.quantity || Number(formData.get("quantity") || 1),
                product_title: response.product_title || "",
                variant_title: response.variant_title || "",
                image: response.image || null,
              },
              formElement: this.form,
            },
          })
        );
        // [End theme custom event] - Variant addition event

        // [Start theme custom event] - Cart change event
        fetch(`${routes.cart_url}.js`)
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);
            document.dispatchEvent(
              new CustomEvent("cart:change", {
                detail: {
                  cart: parsedState,
                },
              })
            );
          })
          .catch((error) => {
            console.error("Error fetching cart state:", error);
          });
        // [End theme custom event] - Cart change event

        let activeElement = document.activeElement;

        // add to cart from quick-view modal
        const quickAddModal = this.closest("quick-view-modal");
        if (quickAddModal && typeof quickAddModal?.hide === "function") {
          activeElement = quickAddModal.openedBy;
          document.body.addEventListener(
            "quick-view-closed",
            () => {
              setTimeout(() => {
                this.cartUIElement.setActiveElement(activeElement);
                this.cartUIElement.renderContents(response);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
          return;
        }

        this.cartUIElement.setActiveElement(activeElement);
        this.cartUIElement.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        if (this.cartUIElement?.closest("cart-notification")) {
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer && cartDrawer.classList.contains("is-empty")) {
            cartDrawer.classList.remove("is-empty");
          }
        }

        if (
          this.cartUIElement?.closest("cart-drawer") &&
          this.cartUIElement?.classList.contains("is-empty")
        ) {
          this.cartUIElement.classList.remove("is-empty");
        }
        if (!this.error) this.submitButton.removeAttribute("aria-disabled");
        this.submitButton.classList.remove("loading");
        if (btnSpinner) btnSpinner.classList.add("hidden");
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    const stickyBarParent = this.closest(".product-sticky-add-bar");
    const horizontalProductParent = this.closest(".product-card-horizontal");
    const errorWrapperSelector = ".product-form__error-message-wrapper";

    this.errorMessageWrapper =
      this.errorMessageWrapper ||
      this.querySelector(errorWrapperSelector) ||
      stickyBarParent?.querySelector(errorWrapperSelector) ||
      horizontalProductParent?.querySelector(errorWrapperSelector);
    if (!this.errorMessageWrapper) return;

    this.errorMessage =
      this.errorMessage ||
      this.errorMessageWrapper.querySelector(".product-form__error-message");
    if (!this.errorMessage) return;

    if (this.errorTimeoutId) {
      clearTimeout(this.errorTimeoutId);
      this.errorTimeoutId = null;
    }

    this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;

      // Auto-hide error message after 3 seconds for product-card-horizontal
      if (horizontalProductParent) {
        this.errorTimeoutId = setTimeout(() => {
          this.errorMessageWrapper.toggleAttribute("hidden", true);
          this.errorMessage.textContent = "";
          this.errorTimeoutId = null;
        }, 3000);
      }
    }
  }
}

if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}

class ProductCard extends HTMLElement {
  // Use this class to define product card-specific functionality, such as slider initialization, variant and swatch switching, and more.
  constructor() {
    super();
    this.mediaSlider = null;
    this.cancelMediaSlider = null;
    this.onPointerEnter = null;
    this.onPointerLeave = null;
    this.onControlsClick = null;
    this.controlsEl = null;
    this.hasManualSelection = false;
    this.onSwatchChange = null;
    this.onCompactSwatchesClick = null;
  }

  connectedCallback() {
    // A grid page can hold dozens of cards: only build the gallery of the ones
    // the shopper is about to see.
    const sliderEl = this.querySelector(".product-card__media--slider");
    if (sliderEl) {
      this.cancelMediaSlider = onNearViewport(sliderEl, () =>
        this.initMediaSlider()
      );
    }
    this.initSwatchQuickView();
  }

  disconnectedCallback() {
    this.cancelMediaSlider?.();
    this.cancelMediaSlider = null;
    this.destroyMediaSlider();
    this.teardownSwatchQuickView();
  }

  initMediaSlider() {
    const sliderEl = this.querySelector(".product-card__media--slider");
    if (!sliderEl || sliderEl.classList.contains("swiper-initialized")) return;
    if (typeof Swiper === "undefined") return;

    const controls = this.querySelector(".product-card__media-controls");
    const prevBtn = controls?.querySelector(".slider-nav-button--prev");
    const nextBtn = controls?.querySelector(".slider-nav-button--next");
    const paginationEl = controls?.querySelector(
      ".product-card__media-pagination"
    );

    this.mediaSlider = new Swiper(sliderEl, {
      loop: false,
      slidesPerView: 1,
      speed: 500,
      watchOverflow: true,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
        disabledClass: "swiper-button-disabled",
      },
      pagination: {
        el: paginationEl,
        clickable: true,
      },
    });

    this.initHoverPreview();
  }

  // Desktop default behaviour: hovering the card previews the second slide and snaps
  // back on leave. Driving the media by hand (pagination bullet or nav arrow) opts the
  // card out of both — the picked slide then stays put, so the chosen images stay
  // comparable across cards.
  initHoverPreview() {
    if (!this.mediaSlider || this.mediaSlider.slides.length < 2) return;
    if (!window.matchMedia || !window.matchMedia("(hover: hover)").matches)
      return;

    this.controlsEl = this.querySelector(".product-card__media-controls");
    if (this.controlsEl) {
      this.onControlsClick = (event) => {
        if (
          event.target.closest(
            ".product-card__media-pagination, .slider-nav-button"
          )
        ) {
          this.hasManualSelection = true;
        }
      };
      this.controlsEl.addEventListener("click", this.onControlsClick);
    }

    this.onPointerEnter = () => {
      if (this.hasManualSelection) return;
      this.mediaSlider?.slideTo(1);
    };
    this.onPointerLeave = () => {
      if (this.hasManualSelection) return;
      this.mediaSlider?.slideTo(0);
    };
    this.addEventListener("mouseenter", this.onPointerEnter);
    this.addEventListener("mouseleave", this.onPointerLeave);
  }

  destroyMediaSlider() {
    if (this.onPointerEnter) {
      this.removeEventListener("mouseenter", this.onPointerEnter);
      this.onPointerEnter = null;
    }
    if (this.onPointerLeave) {
      this.removeEventListener("mouseleave", this.onPointerLeave);
      this.onPointerLeave = null;
    }
    if (this.controlsEl && this.onControlsClick) {
      this.controlsEl.removeEventListener("click", this.onControlsClick);
    }
    this.controlsEl = null;
    this.onControlsClick = null;
    this.hasManualSelection = false;
    if (this.mediaSlider) {
      this.mediaSlider.destroy(true, true);
      this.mediaSlider = null;
    }
  }

  // Clicking a colour swatch opens the Quick view modal pre-selected to that variant
  // (or the connected product). The modal reads the opener's data-product-url at click
  // time and fetches it synchronously, so the base URL is restored immediately after.
  initSwatchQuickView() {
    const opener = this.querySelector(
      ".product-card__quick-view modal-opener button"
    );
    const swatches = this.querySelectorAll(
      ".product-card__color-swatches input[type='radio']"
    );
    if (!opener || !swatches.length) return;

    const baseUrl = opener.dataset.productUrl || "";

    const openQuickView = (input) => {
      const connected = (input.dataset.connectedProductUrl || "").trim();
      const variantId = (input.dataset.variantId || "").trim();
      let url = baseUrl;
      if (connected) {
        url = connected;
      } else if (variantId) {
        url = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}variant=${variantId}`;
      }
      if (!url) return;
      opener.dataset.productUrl = url;
      opener.click();
      opener.dataset.productUrl = baseUrl;
    };

    this.onCompactSwatchesClick = (event) => {
      if (!event.target.closest(".compact_color_swatches")) return;
      opener.dataset.productUrl = baseUrl;
      opener.click();
    };
    this.addEventListener("click", this.onCompactSwatchesClick);

    // A pointer click lands on the swatch label; the browser then forwards a
    // synthetic click to the radio, and Safari reports detail 0 on that one — so
    // the label is the only target both engines agree came from a pointer.
    // Keyboard activation never reaches the label and stays with onSwatchKeydown.
    this.onSwatchClick = (event) => {
      if (event.detail === 0) return;
      const target = event.target;
      if (!target || typeof target.closest !== "function") return;
      const label = target.closest(".product-card__color-swatches label[for]");
      const input = label?.control;
      if (!input || !input.matches("input[type='radio']")) return;
      openQuickView(input);
    };
    this.addEventListener("click", this.onSwatchClick);

    this.onSwatchKeydown = (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const input = event.target;
      if (
        !input ||
        typeof input.matches !== "function" ||
        !input.matches("input[type='radio']") ||
        !input.closest(".product-card__color-swatches")
      )
        return;
      event.preventDefault(); // prevent Space from scrolling and suppress the subsequent synthetic click
      openQuickView(input);
    };
    this.addEventListener("keydown", this.onSwatchKeydown);
  }

  teardownSwatchQuickView() {
    if (this.onCompactSwatchesClick) {
      this.removeEventListener("click", this.onCompactSwatchesClick);
      this.onCompactSwatchesClick = null;
    }
    if (this.onSwatchClick) {
      this.removeEventListener("click", this.onSwatchClick);
      this.onSwatchClick = null;
    }
    if (this.onSwatchKeydown) {
      this.removeEventListener("keydown", this.onSwatchKeydown);
      this.onSwatchKeydown = null;
    }
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.recommendationsSlider = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this.initProductRecommendations();
  }

  initProductRecommendations() {
    this.enableLoading();

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");
        if (recommendations && recommendations.innerHTML.trim().length) {
          //this.innerHTML = recommendations.innerHTML;
          this.replaceChildren(...recommendations.children);
        }

        if (recommendations.hasAttribute("data-section-has-card-slider")) {
          this.setAttribute("data-section-has-card-slider", "true");
          initSectionSliderLayout(this);
        } else if (recommendations.hasAttribute("data-is-complementary")) {
          this.initComplementarySlider();
        }

        if (recommendations.hasAttribute("data-products-count")) {
          this.setAttribute(
            "data-products-count",
            recommendations.getAttribute("data-products-count")
          );
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.disableLoading();
        this._initialized = true;
      });
  }

  initComplementarySlider() {
    const sliderEl = this.querySelector(".js-complementary-slider");
    if (!sliderEl) return;

    const prevBtn = this.querySelector(".slider-nav-button--prev");
    const nextBtn = this.querySelector(".slider-nav-button--next");

    const sliderSettings = {
      loop: false,
      slidesPerView: 1,
      spaceBetween: 8,
      speed: 800,
      watchSlideProgress: true,
      allowTouchMove: true,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
        disabledClass: "swiper-button-disabled",
      },
    };

    new Swiper(sliderEl, sliderSettings);
  }

  enableLoading() {
    const loadingEl = this.querySelector(".product-recommendations-loading");
    if (loadingEl) loadingEl.style.display = "flex";
  }

  disableLoading() {
    const loadingEl = this.querySelector(".product-recommendations-loading");
    if (loadingEl) loadingEl.remove();
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector(
        'input[name="locale_code"], input[name="country_code"]'
      ),
      button: this.querySelector("button"),
      panel: this.querySelector("ul"),
    };

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.elements.button.addEventListener("click", this.togglePanel.bind(this));
    this.addEventListener("keydown", this.onEscapePress.bind(this));

    this.querySelectorAll("a").forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this))
    );
  }

  connectedCallback() {
    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.hidePanel();
    }
  }

  hidePanel() {
    this.elements.button.setAttribute("aria-expanded", "false");
    this.elements.panel.setAttribute("hidden", true);
  }

  showPanel() {
    this.elements.button.setAttribute("aria-expanded", "true");
    this.elements.panel.removeAttribute("hidden");
  }

  togglePanel() {
    if (this.elements.button.getAttribute("aria-expanded") === "true") {
      this.hidePanel();
    } else {
      this.showPanel();
    }
  }

  onEscapePress(event) {
    if (event.key === "Escape") {
      this.hidePanel();
    }
  }

  onItemClick(event) {
    event.preventDefault();
    this.elements.input.value = event.currentTarget.dataset.value;
    this.querySelector("form")?.submit();
    this.hidePanel();
  }
}

if (!customElements.get("localization-form")) {
  customElements.define("localization-form", LocalizationForm);
}

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.enableMockTimer =
      this.getAttribute("data-enable-mock-timer") === "true";

    if (this.enableMockTimer) {
      // get data for infinite mock timer
      const INFINITE_DAYS = Number(this.getAttribute("data-mock-end-day") || 2);
      const DIFF_HOURS = Number(this.getAttribute("data-mock-end-hour") || 3);
      const now = new Date();
      const futureDate = new Date(
        now.getTime() + (INFINITE_DAYS * 24 + DIFF_HOURS) * 60 * 60 * 1000
      );
      const year = futureDate.getFullYear();
      const month = String(futureDate.getMonth() + 1).padStart(2, "0");
      const day = String(futureDate.getDate()).padStart(2, "0");
      const hours = String(futureDate.getHours()).padStart(2, "0");
      const minutes = String(futureDate.getMinutes()).padStart(2, "0");
      this.endDate = `${year}-${month}-${day}`;
      this.endTime = `${hours}:${minutes}`;
    } else {
      // get data from section settings
      this.endDate = this.getAttribute("data-end-date");
      this.endTime = this.getAttribute("data-end-time");
    }
    // ----------------------------------------------------------------
    this.interval;
    this.isStarted = false;
    this.setInterval(this.endDate, this.endTime);
  }

  onInit(endDate, endTime) {
    this.completeBehavior = this.getAttribute("data-complete-behavior");
    this.parentEl = this.closest(".block-countdown-timer");
    this.countdownTimerEl = this.querySelector(".countdown-timer__timer");
    this.countdownEndEl = this.querySelector(".countdown-timer__end");
    this.daysEl = this.querySelector(".js-countdown-timer-days");
    this.hoursEl = this.querySelector(".js-countdown-timer-hours");
    this.minutesEl = this.querySelector(".js-countdown-timer-minutes");
    this.secondsEl = this.querySelector(".js-countdown-timer-seconds");
    // ----------------------------------------------------------------
    const countdownDate = new Date(`${endDate}T${endTime}`);
    const now = new Date();
    const distance = countdownDate.getTime() - now.getTime();
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    // ----------------------------------------------------------------
    if (distance < 0 && this.completeBehavior === "hide_block") {
      if (this.parentEl) {
        this.parentEl.style.display = "none";
      }
      this.style.display = "none";
      clearInterval(this.interval);
    } else if (distance < 0 && this.completeBehavior === "show_message") {
      if (this.countdownTimerEl) {
        this.countdownTimerEl.style.display = "none";
      }
      if (this.countdownEndEl) {
        this.countdownEndEl.style.display = "flex";
      }
      clearInterval(this.interval);
    } else {
      this.setTimeValue(this.daysEl, this.getTwoDigitTime(days));
      this.setTimeValue(this.hoursEl, this.getTwoDigitTime(hours));
      this.setTimeValue(this.minutesEl, this.getTwoDigitTime(minutes));
      this.setTimeValue(this.secondsEl, this.getTwoDigitTime(seconds));
    }
  }

  // Swap the digit inside a time slot: the old one animates out upwards while a
  // clone carrying the new value rises into its place.
  setTimeValue(el, value) {
    if (!el) return;

    const next = String(value);
    const current = el.querySelector(
      ".countdown-timer__digit:not(.countdown-timer__digit--out)"
    );

    if (!current) {
      el.textContent = next;
      return;
    }

    if (current.textContent === next) return;

    // Drop any digit left behind by a swap whose animation never completed
    // (e.g. the tab was in the background).
    el.querySelectorAll(".countdown-timer__digit--out").forEach((node) =>
      node.remove()
    );

    const incoming = current.cloneNode(false);
    incoming.textContent = next;
    incoming.classList.remove("countdown-timer__digit--out");
    incoming.classList.add("countdown-timer__digit--in");
    incoming.addEventListener(
      "animationend",
      () => incoming.classList.remove("countdown-timer__digit--in"),
      { once: true }
    );

    current.classList.remove("countdown-timer__digit--in");
    current.classList.add("countdown-timer__digit--out");
    current.addEventListener("animationend", () => current.remove(), {
      once: true,
    });

    el.appendChild(incoming);
  }

  getTwoDigitTime(time) {
    if (String(time).length === 1) {
      return "0" + time;
    }
    if (String(time) === "-1") {
      return "00";
    }
    return time;
  }

  setInterval(endDate, endTime) {
    clearInterval(this.interval);
    this.interval = setInterval(this.onInit.bind(this, endDate, endTime), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }
}

if (!customElements.get("countdown-timer")) {
  customElements.define("countdown-timer", CountdownTimer);
}

// Newsletter: if Shopify redirected back with ?contact_posted=true, mark all
// newsletter submit buttons as successful (fallback for when Liquid form.posted_successfully?
// does not fire, e.g. page cached or duplicate email submission).
(function () {
  var params = new URLSearchParams(window.location.search);
  if (params.get("contact_posted") !== "true") return;

  document.querySelectorAll(".newsletter-form__button").forEach(function (btn) {
    btn.classList.add("newsletter-form__button--success");
  });

  document
    .querySelectorAll(".newsletter-form__wrapper")
    .forEach(function (wrapper) {
      wrapper.classList.add("newsletter-form__wrapper--success");
    });

  // Clean up the param so refreshing the page doesn't re-show the success state
  params.delete("contact_posted");
  var qs = params.toString();
  var cleanUrl =
    window.location.pathname + (qs ? "?" + qs : "") + window.location.hash;
  history.replaceState(null, "", cleanUrl);
})();

// Unified form error state: the browser's own constraint validation drives the
// shared .is-invalid styles (form.scss), so no form wires its own inline hooks.
(function () {
  function clearInvalid(event) {
    var control = event.target;
    if (!control.classList || !control.classList.contains("is-invalid")) return;
    if (
      typeof control.checkValidity !== "function" ||
      control.checkValidity()
    ) {
      control.classList.remove("is-invalid");
    }
  }

  document.addEventListener(
    "invalid",
    function (event) {
      if (event.target.classList) event.target.classList.add("is-invalid");
    },
    true
  );

  document.addEventListener("input", clearInvalid);
  document.addEventListener("change", clearInvalid);
})();

// Drag-to-scroll: mouse-drag support for any horizontally scrolling container
// marked `.js-drag-scroll` (e.g. the product-compare table) — touch and pen
// already pan such containers natively. Delegated from the document since
// these rows can be re-rendered or live inside sections loaded later; one
// page-level listener set never needs re-binding or teardown.
(function () {
  var ROW = ".js-drag-scroll";
  var row = null;
  var startX = 0;
  var startScroll = 0;
  var dragged = false;

  function isScrollable(el) {
    return el.scrollWidth > el.clientWidth;
  }

  // The grab cursor is only offered while the row actually overflows; checked on
  // entry because the overflow depends on the viewport and the block count.
  document.addEventListener("pointerover", function (event) {
    if (event.pointerType !== "mouse") return;

    var target = event.target.closest(ROW);
    if (target) target.classList.toggle("is-scrollable", isScrollable(target));
  });

  function onPointerMove(event) {
    if (!row) return;

    var delta = event.clientX - startX;
    if (Math.abs(delta) > 3) dragged = true;
    row.scrollLeft = startScroll - delta;
  }

  function onPointerUp() {
    document.removeEventListener("pointermove", onPointerMove);
    if (row) row.classList.remove("is-dragging");
    row = null;
    // The click lands right after this, so the flag is cleared behind it.
    setTimeout(function () {
      dragged = false;
    });
  }

  document.addEventListener("pointerdown", function (event) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;

    var target = event.target.closest(ROW);
    if (!target || !isScrollable(target)) return;
    // A 3D viewer rotates and a video seeks on drag, so the row must not scroll underneath them.
    if (event.target.closest("model-viewer, video")) return;

    row = target;
    startX = event.clientX;
    startScroll = target.scrollLeft;
    dragged = false;
    target.classList.add("is-dragging");

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp, { once: true });
  });

  // A drag must not activate a link/button it happens to end on.
  document.addEventListener(
    "click",
    function (event) {
      if (!dragged || !event.target.closest(ROW)) return;

      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  // Links and images would otherwise start a native drag.
  document.addEventListener("dragstart", function (event) {
    if (event.target.closest(ROW)) event.preventDefault();
  });
})();
